import type { CombatLogEntry, RunStats } from "../game/combatLog";
import {
  formatCombatLogText,
  formatMissionTime,
} from "../game/combatLog";
import { formatSurviveTime } from "../game/endlessScore";
import { verseById } from "../data/verses";

function formatTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export type EndScreenShowOpts = {
  won: boolean;
  lastMessage: string;
  stats: RunStats;
  log: CombatLogEntry[];
  /** Player-facing defeat/victory cause line. */
  cause?: string;
  /** Free-play endless survival results */
  endless?: {
    survived: number;
    best: number;
    isNewBest: boolean;
    /** Optional note under the score (packs, collection, etc.). */
    note?: string;
    /** Stable protocol checkpoint available for full-state resume. */
    checkpoint?: { label: string; elapsed: number } | null;
  };
  campaign?: {
    /** Show Return to map / next stage CTAs. */
    enabled: boolean;
    nextMissionName?: string | null;
  };
};

type Moment = { label: string; value: string; hint?: string };

type Mood = "won" | "lost" | "best" | "endless";

function topVerseId(order: string[]): string | null {
  if (!order.length) return null;
  const counts = new Map<string, number>();
  for (const id of order) {
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  let bestId = order[0]!;
  let bestN = 0;
  for (const [id, n] of counts) {
    if (n > bestN) {
      bestN = n;
      bestId = id;
    }
  }
  return bestId;
}

function verseLabel(id: string): string {
  return verseById(id)?.reference ?? id;
}

/** Short, player-facing line that reacts to how the fight went. */
function flavorLine(opts: EndScreenShowOpts): string {
  const s = opts.stats;
  const endless = opts.endless;

  if (endless) {
    if (endless.isNewBest) {
      return "A new mark. The Word outlasted the storm a little longer.";
    }
    if (endless.best > 0 && endless.survived >= endless.best * 0.85) {
      return "Close to your best — one more steady stand.";
    }
    if (s.versesPlayed === 0) {
      return "The board moved before a verse left your lips. Next run: speak early.";
    }
    if (s.liesSilenced >= 8) {
      return "Many lies fell silent before the Will gave way.";
    }
    return "Retreat is not failure — rest, then hold the line again.";
  }

  if (opts.won) {
    if (s.minIntegrity >= 70) {
      return "The Secret Place barely trembled. Steady under pressure.";
    }
    if (s.minIntegrity > 0 && s.minIntegrity < 22) {
      return "Held by a thread — and by the Word.";
    }
    if (s.redeems >= 2) {
      return "What the enemy meant for harm, you redeemed more than once.";
    }
    if (s.liesSilenced >= 6) {
      return "Lie after lie fell silent. You held the line.";
    }
    if (s.prayersPlaced >= 3) {
      return "Prayer stayed on the board. The line held.";
    }
    return "Integrity held until the hour closed. Well stood.";
  }

  // Defeat / retreat
  if (s.elapsed >= 90 && s.liesSilenced >= 4) {
    return "You stood longer than it felt. The Word still cut through.";
  }
  if (s.versesPlayed <= 1) {
    return "Speak sooner next time — especially on the Will.";
  }
  if (s.minIntegrity <= 0 && s.liesSilenced >= 3) {
    return "The lies felt Scripture even so. Clear Will first next fight.";
  }
  return "Back to the Secret Place. Catch your breath, then answer again.";
}

function buildMoments(opts: EndScreenShowOpts): Moment[] {
  const s = opts.stats;
  const endless = opts.endless;
  const moments: Moment[] = [];

  if (endless) {
    moments.push({
      label: "Survived",
      value: formatSurviveTime(endless.survived),
      hint: endless.isNewBest ? "Personal best" : undefined,
    });
    if (endless.best > 0 && !endless.isNewBest) {
      moments.push({
        label: "Best",
        value: formatSurviveTime(endless.best),
      });
    }
  } else {
    moments.push({
      label: opts.won ? "Held for" : "Stood for",
      value: formatTime(s.elapsed),
      hint: s.duration > 0 ? `of ${formatTime(s.duration)}` : undefined,
    });
  }

  moments.push({
    label: "Lies silenced",
    value: String(s.liesSilenced),
    hint:
      s.liesSpawned > 0
        ? `${s.liesSpawned} entered`
        : undefined,
  });

  moments.push({
    label: "Verses spoken",
    value: String(s.versesPlayed),
    hint:
      s.versesPlayed > 0
        ? `${s.instantsPlayed} answer · ${s.placeablesPlayed} abide`
        : undefined,
  });

  // Closest call is dramatic when it mattered
  const minI = Math.round(s.minIntegrity);
  if (!endless || minI > 0) {
    moments.push({
      label: minI < 25 && minI > 0 ? "Closest call" : "Integrity low",
      value: String(minI),
      hint: minI < 25 && minI > 0 ? "Nearly broken" : "Lowest this fight",
    });
  } else if (s.redeems > 0) {
    moments.push({
      label: "Redeemed",
      value: String(s.redeems),
    });
  }

  return moments.slice(0, 4);
}

export class EndScreen {
  private root = document.getElementById("end-overlay")!;
  private card = this.root.querySelector(".end-card") as HTMLElement | null;
  private titleEl = document.getElementById("end-title")!;
  private messageEl = document.getElementById("end-message")!;
  private summaryEl = document.getElementById("end-summary");
  private logCountEl = document.getElementById("end-log-count");
  private logPre = document.getElementById("end-log");
  private logDetails = document.getElementById(
    "end-log-details",
  ) as HTMLDetailsElement | null;
  private copyBtn = document.getElementById(
    "btn-copy-log",
  ) as HTMLButtonElement | null;
  private actionsEl = document.querySelector(
    "#end-overlay .end-actions",
  ) as HTMLElement | null;

  private lastText = "";

  constructor(
    private readonly handlers?: {
      onMenu?: () => void;
      onRestart?: () => void;
      /** Free-play: resume farthest stable protocol snapshot. */
      onResume?: () => void;
      onCampaign?: () => void;
      onNextStage?: () => void;
    },
  ) {
    this.copyBtn?.addEventListener("click", () => {
      if (!this.lastText) return;
      void navigator.clipboard?.writeText(this.lastText).then(
        () => {
          if (this.copyBtn) {
            const prev = this.copyBtn.textContent;
            this.copyBtn.textContent = "Copied";
            window.setTimeout(() => {
              if (this.copyBtn) this.copyBtn.textContent = prev ?? "Copy log";
            }, 1200);
          }
        },
        () => {
          const range = document.createRange();
          if (this.logPre) {
            range.selectNodeContents(this.logPre);
            const sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(range);
          }
        },
      );
    });

    this.actionsEl?.addEventListener("click", (e) => {
      const t = (e.target as HTMLElement).closest("[data-end-act]") as
        | HTMLElement
        | null;
      if (!t) return;
      const act = t.dataset.endAct;
      if (act === "menu") this.handlers?.onMenu?.();
      if (act === "restart") this.handlers?.onRestart?.();
      if (act === "resume") this.handlers?.onResume?.();
      if (act === "campaign") this.handlers?.onCampaign?.();
      if (act === "next") this.handlers?.onNextStage?.();
    });
  }

  show(opts: EndScreenShowOpts): void {
    const endless = opts.endless;
    const mood: Mood = endless
      ? endless.isNewBest
        ? "best"
        : "endless"
      : opts.won
        ? "won"
        : "lost";

    this.card?.classList.remove(
      "end-card--won",
      "end-card--lost",
      "end-card--best",
      "end-card--endless",
    );
    this.card?.classList.add(`end-card--${mood}`);

    this.titleEl.textContent = endless
      ? endless.isNewBest
        ? "New best survival"
        : "Run over"
      : opts.won
        ? "You held the line"
        : "Retreated to the Secret Place";

    const cause =
      opts.cause ??
      (endless
        ? `Survived ${formatSurviveTime(endless.survived)} — integrity reached 0.`
        : opts.won
          ? "You held integrity until the mission timer ended."
          : "Integrity reached 0 — a lie held the Will.");

    this.messageEl.innerHTML = "";
    this.messageEl.className = "end-message";

    if (endless) {
      const score = document.createElement("p");
      score.className = "end-score";
      score.innerHTML = `<strong>Score</strong> ${formatSurviveTime(endless.survived)}`;
      this.messageEl.appendChild(score);
      if (endless.isNewBest) {
        const nb = document.createElement("p");
        nb.className = "end-new-best";
        nb.textContent = "Personal best!";
        this.messageEl.appendChild(nb);
      } else if (endless.best > 0) {
        const best = document.createElement("p");
        best.className = "end-best";
        best.textContent = `Best (${opts.stats.difficulty}): ${formatSurviveTime(endless.best)}`;
        this.messageEl.appendChild(best);
      }
      if (endless.checkpoint) {
        const cp = document.createElement("p");
        cp.className = "end-checkpoint-note";
        cp.textContent = `Checkpoint ready · ${endless.checkpoint.label} · ${formatSurviveTime(endless.checkpoint.elapsed)} (full board state)`;
        this.messageEl.appendChild(cp);
      }
      if (endless.note) {
        const note = document.createElement("p");
        note.className = "end-pack-note";
        note.textContent = endless.note;
        this.messageEl.appendChild(note);
      }
    }

    const causeEl = document.createElement("p");
    causeEl.className = "end-cause";
    causeEl.textContent = cause;
    this.messageEl.appendChild(causeEl);

    const flavor = document.createElement("p");
    flavor.className = "end-flavor";
    flavor.textContent = flavorLine(opts);
    this.messageEl.appendChild(flavor);

    if (this.summaryEl) {
      this.summaryEl.replaceChildren();

      // Highlight strip — what players actually feel proud of
      const moments = buildMoments(opts);
      const grid = document.createElement("div");
      grid.className = "end-moments";
      grid.setAttribute("aria-label", "Fight highlights");
      for (const m of moments) {
        const cell = document.createElement("div");
        cell.className = "end-moment";
        const lab = document.createElement("span");
        lab.className = "end-moment-label";
        lab.textContent = m.label;
        const val = document.createElement("span");
        val.className = "end-moment-value";
        val.textContent = m.value;
        cell.append(lab, val);
        if (m.hint) {
          const hint = document.createElement("span");
          hint.className = "end-moment-hint";
          hint.textContent = m.hint;
          cell.appendChild(hint);
        }
        grid.appendChild(cell);
      }
      this.summaryEl.appendChild(grid);

      // Signature verse of the fight
      const topId = topVerseId(opts.stats.versePlayOrder);
      if (topId) {
        const counts = opts.stats.versePlayOrder.filter((id) => id === topId)
          .length;
        const sword = document.createElement("div");
        sword.className = "end-sword";
        const label = document.createElement("span");
        label.className = "end-sword-label";
        label.textContent =
          counts > 1 ? "Most proclaimed" : "Your sword this fight";
        const ref = document.createElement("span");
        ref.className = "end-sword-ref";
        ref.textContent = verseLabel(topId);
        sword.append(label, ref);
        if (counts > 1) {
          const n = document.createElement("span");
          n.className = "end-sword-count";
          n.textContent = `×${counts}`;
          sword.appendChild(n);
        }
        this.summaryEl.appendChild(sword);
      }

      // Technical dump stays available, not front-and-center
      const details = document.createElement("details");
      details.className = "end-details";
      const summary = document.createElement("summary");
      summary.textContent = "Run details";
      details.appendChild(summary);

      const s = opts.stats;
      const rows: [string, string][] = [
        [
          "Outcome",
          endless
            ? endless.isNewBest
              ? "New best"
              : "Survived"
            : opts.won
              ? "Victory"
              : "Retreated",
        ],
        ["Scenario", s.scenarioName],
        ["Difficulty", s.difficulty],
        endless
          ? [
              "Score (time)",
              `${formatSurviveTime(s.elapsed)} (${s.elapsed.toFixed(1)}s)`,
            ]
          : [
              "Time",
              `${formatTime(s.elapsed)} / ${formatTime(s.duration)}`,
            ],
        ...(endless && endless.best > 0
          ? ([["Best", formatSurviveTime(endless.best)]] as [string, string][])
          : []),
        [
          "Integrity",
          `${Math.round(s.integrityEnd)} end · min ${Math.round(s.minIntegrity)}`,
        ],
        [
          "Verses",
          `${s.versesPlayed} (${s.instantsPlayed} answer · ${s.placeablesPlayed} abide)`,
        ],
        [
          "Lies",
          `${s.liesSilenced} silenced / ${s.liesSpawned} entered · ${s.lieLevelDrops} drops`,
        ],
        [
          "Prayers",
          `${s.prayersPlaced} placed · ${s.prayersBroken} broken · ${s.redeems} redeemed`,
        ],
        ["Study / draws", `${s.reveals} studies · ${s.paidDraws} paid draws`],
      ];

      const dl = document.createElement("dl");
      dl.className = "end-stats";
      for (const [k, v] of rows) {
        const dt = document.createElement("dt");
        dt.textContent = k;
        const dd = document.createElement("dd");
        dd.textContent = v;
        dl.append(dt, dd);
      }
      details.appendChild(dl);

      if (s.versePlayOrder.length) {
        const plays = document.createElement("p");
        plays.className = "end-play-order";
        plays.textContent = `Play order: ${s.versePlayOrder
          .map(verseLabel)
          .join(" → ")}`;
        details.appendChild(plays);
      }

      this.summaryEl.appendChild(details);
    }

    this.lastText = this.buildCopyText(opts.stats, opts.log, opts.endless);
    if (this.logCountEl) {
      this.logCountEl.textContent = String(opts.log.length);
    }
    if (this.logPre) {
      this.logPre.textContent = opts.log
        .map((e) => {
          const at =
            e.origin != null
              ? `@${e.origin.layer}${e.origin.slot !== undefined ? `:${e.origin.slot + 1}` : ""}`
              : "       ";
          return `${formatMissionTime(e.t).padStart(7)}  #${String(e.seq).padStart(3, " ")}  [${e.kind.padEnd(6)}] ${at.padEnd(10)} ${e.message}`;
        })
        .join("\n");
    }
    if (this.logDetails) this.logDetails.open = false;

    this.renderActions(opts);
    this.root.classList.remove("hidden");
    // Retrigger enter animation
    this.card?.classList.remove("end-card--enter");
    void this.card?.offsetWidth;
    this.card?.classList.add("end-card--enter");
  }

  private renderActions(opts: EndScreenShowOpts): void {
    if (!this.actionsEl) return;
    const campaign = opts.campaign?.enabled;
    const nextName = opts.campaign?.nextMissionName;
    const parts: string[] = [];

    parts.push(
      `<button type="button" class="btn secondary" data-end-act="menu">Main menu</button>`,
    );

    if (campaign) {
      parts.push(
        `<button type="button" class="btn ${opts.won && !nextName ? "primary" : "secondary"}" data-end-act="campaign">Return to map</button>`,
      );
      if (opts.won && nextName) {
        parts.push(
          `<button type="button" class="btn primary" data-end-act="next">Next · ${escapeHtml(nextName)}</button>`,
        );
      }
      parts.push(
        `<button type="button" class="btn secondary" data-end-act="restart">Play again</button>`,
      );
    } else if (opts.endless?.checkpoint) {
      const label = escapeHtml(opts.endless.checkpoint.label);
      parts.push(
        `<button type="button" class="btn primary" data-end-act="resume" title="Full board state at this protocol">Resume · ${label}</button>`,
      );
      parts.push(
        `<button type="button" class="btn secondary" data-end-act="restart">New run</button>`,
      );
    } else {
      parts.push(
        `<button type="button" class="btn primary" data-end-act="restart">Play again</button>`,
      );
    }

    this.actionsEl.innerHTML = parts.join("");
  }

  hide(): void {
    this.root.classList.add("hidden");
    this.card?.classList.remove(
      "end-card--won",
      "end-card--lost",
      "end-card--best",
      "end-card--endless",
      "end-card--enter",
    );
  }

  private buildCopyText(
    stats: RunStats,
    log: CombatLogEntry[],
    endless?: EndScreenShowOpts["endless"],
  ): string {
    const timeLine = endless
      ? `Score (survived): ${formatSurviveTime(stats.elapsed)} (${stats.elapsed.toFixed(2)}s)` +
        (endless.best > 0
          ? `\nBest: ${formatSurviveTime(endless.best)}${endless.isNewBest ? " (NEW)" : ""}`
          : "")
      : `Elapsed: ${stats.elapsed.toFixed(2)}s / ${stats.duration}s`;
    const header = [
      `Sword of the Spirit — Proclaim`,
      `Outcome: ${endless ? "endless" : stats.status}`,
      `Scenario: ${stats.scenarioName} (${stats.scenarioId})`,
      `Difficulty: ${stats.difficulty}`,
      timeLine,
      `Integrity: end ${stats.integrityEnd.toFixed(1)} min ${stats.minIntegrity.toFixed(1)}`,
      `Verses proclaimed: ${stats.versesPlayed} (instant ${stats.instantsPlayed}, placeable ${stats.placeablesPlayed})`,
      `Lies: spawned ${stats.liesSpawned} silenced ${stats.liesSilenced} drops ${stats.lieLevelDrops}`,
      `Prayers: placed ${stats.prayersPlaced} broken ${stats.prayersBroken} redeems ${stats.redeems}`,
      `Studies: ${stats.reveals}  Recalls: ${stats.paidDraws}`,
      `Play order: ${stats.versePlayOrder.map(verseLabel).join(", ") || "(none)"}`,
      `--- combat log ---`,
      formatCombatLogText(log),
    ];
    return header.join("\n");
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
